import { useMemo } from "react";

import type { ChartSeriesData, Sensor } from "@core/apis/shared";

import {
  combineFormattedData,
  formatMeterComponentData,
  formatSensorData,
  formatWeatherData,
} from "../utils/dataFormatters";
import type { RadiatorSensorDataMap } from "./useRadiatorCurveData";

type SeriesLabels = {
  radiatorMeter: string;
  radiatorStage: string;
  weatherMeter: string;
  weatherStage: string;
};

const DEFAULT_LABELS: SeriesLabels = {
  radiatorMeter: "Radiator System",
  radiatorStage: "Internal",
  weatherMeter: "Weather Station",
  weatherStage: "External",
};

type UseFormattedEnergyDataArgs = {
  /** Raw meter data from MDSL API */
  rawMeterData: any[] | undefined;
  meterDataLoading: boolean;
  /** Radiator sensors from useRadiatorCurveData */
  radiatorSensors: Sensor[];
  /** Sensor data keyed by uid from useRadiatorCurveData */
  radiatorSensorData: RadiatorSensorDataMap;
  radiatorSensorDataLoading: boolean;
  /** Weather data points from weather API or useRadiatorCurveData */
  weatherData: Array<{ datetime: string; value: number }> | undefined;
  weatherLoading: boolean;
  /** Display labels for radiator/weather series metadata */
  labels?: Partial<SeriesLabels>;
};

type UseFormattedEnergyDataResult = {
  data: ChartSeriesData[];
  isLoading: boolean;
  isEmpty: boolean;
};

/**
 * Transforms raw data from 3 sources (MDSL meter data, radiator sensors,
 * weather) into a unified ChartSeriesData[] for energy consumption charts.
 */
export function useFormattedEnergyData({
  rawMeterData,
  meterDataLoading,
  radiatorSensors,
  radiatorSensorData,
  radiatorSensorDataLoading,
  weatherData,
  weatherLoading,
  labels: labelOverrides,
}: UseFormattedEnergyDataArgs): UseFormattedEnergyDataResult {
  const radiatorMeter = labelOverrides?.radiatorMeter ?? DEFAULT_LABELS.radiatorMeter;
  const radiatorStage = labelOverrides?.radiatorStage ?? DEFAULT_LABELS.radiatorStage;
  const weatherMeter = labelOverrides?.weatherMeter ?? DEFAULT_LABELS.weatherMeter;
  const weatherStage = labelOverrides?.weatherStage ?? DEFAULT_LABELS.weatherStage;

  const data = useMemo(() => {
    const meterDataFormatted: (ChartSeriesData | null)[] = [];

    if (rawMeterData && Array.isArray(rawMeterData)) {
      rawMeterData.forEach((component) => {
        meterDataFormatted.push(formatMeterComponentData(component));
      });
    }

    const radiatorSupplySensorUids = radiatorSensors
      .filter((sensor) => sensor.sensor_type_technical_name === "radiator_system_supply_temp")
      .map((sensor) => sensor.uid);

    const radiatorFormatted = formatSensorData(
      radiatorSensorData,
      radiatorSupplySensorUids,
      "radiator_system_supply_temp",
      {
        uid: "radiator-supply-temp",
        meter: radiatorMeter,
        stage: radiatorStage,
        component: "radiator_system_supply_temp",
        variant: "temperature",
      }
    );

    const outdoorFormatted = formatWeatherData(weatherData ?? [], {
      uid: "outdoor-temp",
      meter: weatherMeter,
      stage: weatherStage,
      component: "outdoor_temp",
      variant: "temperature",
    });

    return combineFormattedData(...meterDataFormatted, radiatorFormatted, outdoorFormatted);
  }, [
    rawMeterData,
    weatherData,
    radiatorSensorData,
    radiatorSensors,
    radiatorMeter,
    radiatorStage,
    weatherMeter,
    weatherStage,
  ]);

  const isLoading = meterDataLoading || weatherLoading || radiatorSensorDataLoading;
  const isEmpty = !isLoading && data.length === 0;

  return { data, isLoading, isEmpty };
}
